import React from "react";
import { X, Sparkles, Volume2, VolumeX, Mic, MicOff } from "lucide-react";
import { track } from "../../lib/analytics";

/**
 * Top bar of the AR mirror — eyebrow + TTS toggle + voice-command
 * toggle + close button. Pure presentational; all state is owned by
 * the parent LiveCoach.
 */
const LiveCoachTopBar = ({
  tts,
  voice,
  effectiveVoiceEnabled,
  onToggleVoice,
  onClose,
}) => (
  <div className="absolute top-0 left-0 right-0 z-30 flex items-center justify-between px-5 pt-[max(env(safe-area-inset-top),20px)] pb-3">
    <div className="text-white/80 text-xs uppercase tracking-[0.25em] flex items-center gap-2">
      <Sparkles className="h-4 w-4 text-[#D5A8A4]" />
      Mirror mode
    </div>
    <div className="flex items-center gap-2">
      {tts.supported && (
        <button
          onClick={() => {
            tts.toggle();
            track("learn_with_trouve_tts_toggled", { enabled: !tts.enabled });
          }}
          className={`h-10 w-10 rounded-full backdrop-blur-md border flex items-center justify-center transition-all ${
            tts.enabled
              ? "bg-[#D5A8A4]/30 border-[#D5A8A4]/60 text-[#FFE6D7] hover:bg-[#D5A8A4]/40"
              : "bg-white/15 border-white/20 text-white hover:bg-white/25"
          }`}
          data-testid="live-coach-tts-toggle"
          aria-label={tts.enabled ? "Mute voice coach" : "Unmute voice coach"}
          aria-pressed={tts.enabled}
          title={tts.enabled ? "Voice coach on — tap to mute" : "Voice coach off — tap to hear steps"}
        >
          {tts.enabled ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
        </button>
      )}
      {/* Voice-command toggle — "Hey Trouvé, next step". Only renders
          when the browser supports the Web Speech API. Visually
          reflects the *effective* state so demo mode shows the active
          glow even though the saved pref is off. */}
      {voice.supported && (
        <button
          onClick={onToggleVoice}
          className={`relative h-10 w-10 rounded-full backdrop-blur-md border flex items-center justify-center transition-all ${
            effectiveVoiceEnabled && voice.listening
              ? "bg-[#D5A8A4]/30 border-[#D5A8A4]/60 text-[#FFE6D7] hover:bg-[#D5A8A4]/40"
              : "bg-white/15 border-white/20 text-white hover:bg-white/25"
          }`}
          data-testid="live-coach-voice-toggle"
          aria-label={
            effectiveVoiceEnabled
              ? "Voice commands on — tap to mute"
              : "Voice commands off — tap to listen for ‘Hey Trouvé, next’"
          }
          aria-pressed={effectiveVoiceEnabled}
          title={
            voice.permissionDenied
              ? "Microphone access denied — enable in browser settings"
              : effectiveVoiceEnabled
              ? "Listening for ‘Hey Trouvé, next’ — tap to mute"
              : "Tap to enable hands-free voice commands"
          }
        >
          {effectiveVoiceEnabled ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}
          {effectiveVoiceEnabled && voice.listening && (
            <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 bg-[#FFE6D7] rounded-full ring-2 ring-[#1a1416] animate-pulse" />
          )}
        </button>
      )}
      <button
        onClick={onClose}
        className="h-10 w-10 rounded-full bg-white/15 backdrop-blur-md border border-white/20 flex items-center justify-center text-white hover:bg-white/25 transition-all"
        data-testid="live-coach-close-btn"
        aria-label="Close mirror"
      >
        <X className="h-5 w-5" />
      </button>
    </div>
  </div>
);

export default LiveCoachTopBar;
